import React, { useState, useEffect } from 'react'

import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Link } from 'react-router-dom'
import axios from '../../../lib/axios'
import Swal from 'sweetalert2'
import { Box, TextField, FormControl, InputLabel, Select, IconButton, MenuItem, FormHelperText, ListItemText, ListItemIcon, Checkbox, Chip, ListSubheader, List, ListItem, Tab, Tooltip } from '@mui/material'
import { createRegexFromString, deleteZero, handleAxiosError, handleNumber, handleZero, isValid } from '../../../helpers/helper'
import ClearIcon from "@mui/icons-material/Clear"
import { PAYMENT_METHODS, SELECT_STYLE, TEXTFIELD_STYLE } from '../../../constants/constants'

import Loading from '../../../components/Loading'
import { useStateContext } from '../../../context/ContextProvider'
import { useAuth } from '../../../hooks/auth'
import gradeCodesAPI from '../../../services/api/GradeCodes/gradeCodesAPI'
import TabContext from '@mui/lab/TabContext'
import TabPanel from '@mui/lab/TabPanel'
import AddRateModal from '../Create/AddRateModal'
import TabList from '@mui/lab/TabList'
import TableShared from '../../../components/TableShared'
import { toNumber } from 'lodash'
import { t } from 'i18next'
import AddButton from '../../../components/AddButton'

const UpdateProductionEmployee = () => {

    const { user } = useAuth({ middleware: 'guest ' })

    const { companies, sites, getSites, config, currencySymbol, pusher } = useStateContext()
    const params = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const [isLoading, setIsLoading] = useState(false)
    const navigate = useNavigate()

    const [companyProductionTypes, setCompanyProductionTypes] = useState([])
    const [typeOptions, setTypeOptions] = useState([])

    /* Fields */
    const [no, setNo] = useState('')
    const [name, setName] = useState('')
    const [companyId, setCompanyId] = useState('')
    const [siteId, setSiteId] = useState('')
    const [productionTypes, setProductionTypes] = useState([])
    const everyProductionTypeSelected = companyProductionTypes.length > 0 && productionTypes.length === companyProductionTypes.length

    const [paymentMethod, setPaymentMethod] = useState('')
    const [rates, setRates] = useState([])
    const [defaultHourlyRate, setDefaultHourlyRate] = useState(0)
    const [gradeCodes, setGradeCodes] = useState([])

    const [errorNo, setErrorNo] = useState(false)
    const [errorName, setErrorName] = useState(false)
    const [errorSite, setErrorSite] = useState(false)
    const [errorCompany, setErrorCompany] = useState(false)
    const [errorProductionTypes, setErrorProductionTypes] = useState(false)

    const [openProductionTypes, setOpenProductionTypes] = useState(false)
    const [openAddRatesModal, setOpenAddRatesModal] = useState(false)

    const [tabValue, setTabValue] = useState(() => {
        if (
            searchParams.has('tab')
            && ['general', 'payroll'].includes(searchParams.get('tab'))
        ) {
            return searchParams.get('tab')
        } else {
            return 'general'
        }
    })

    useEffect(() => {
        getGradeCodes()
    }, [])

    useEffect(() => {
        const channelProductionEmployeeDelete = pusher.subscribe(`${localStorage.getItem('client_id')}-productionemployee-deleted`)
        const channelProductionEmployeeUpdate = pusher.subscribe(`${localStorage.getItem('client_id')}-productionemployee-updated`)

        channelProductionEmployeeDelete.bind(`${localStorage.getItem('client_id')}-productionemployee-deleted-event`, data => {
            try {
                if(data.id == params.id) {
                    navigate('/production-employees', {
                        replace: true
                    })
                }
            } catch (e) {
                console.info("Could not handle delete event: ", e.message)
            }
        })

        channelProductionEmployeeUpdate.bind(`${localStorage.getItem('client_id')}-productionemployee-updated-event`, data => {
            try {
                if(data.id == params.id) {
                    getProductionEmployee(data.id);
                }
            } catch (e) {
                console.info("Could not handle update event: ", e.message)
            }
        })

        return (() => {
            pusher.unsubscribe(`${localStorage.getItem('client_id')}-productionemployee-deleted`)
            pusher.unsubscribe(`${localStorage.getItem('client_id')}-productionemployee-updated`)
        })
    }, [])

    useEffect(() => {
        function handleKeyDown(e) {
            if (e.keyCode == '27') {
                navigate(-1)
            }
        }

        document.addEventListener('keydown', handleKeyDown);

        return function cleanup() {
            document.removeEventListener('keydown', handleKeyDown);
        }
    }, []);

    useEffect(() => {
        getProductionEmployee(params.id)
    }, [params.id])

    useEffect(() => {
        setSearchParams({ tab: tabValue })
    }, [tabValue])

    useEffect(() => {
        if (companyId) {
            getProductionTypes(companyId)
            getSites(companyId)
        }
    }, [companyId])

    useEffect(() => {
        setTypeOptions(companyProductionTypes)
    }, [companyProductionTypes, productionTypes])

    const getProductionEmployee = async (id) => {
        setIsLoading(true)
        await axios.get(`/api/production-employees/${id}?with=production_types,rates,defaultHourlyRates`, config)
            .then(res => {
                const productionEmployee = res.data
                setNo(productionEmployee.production_employee_no)
                setName(productionEmployee.name)
                setProductionTypes(productionEmployee.production_types)
                setCompanyId(productionEmployee.company_id)
                setSiteId(productionEmployee.site_id)
                setPaymentMethod(productionEmployee?.payment_method)
                setRates(productionEmployee?.rates)
                setDefaultHourlyRate(productionEmployee?.default_hourly_rates?.length > 0 ? productionEmployee.default_hourly_rates[0].hourly_rate : 0);
            })
            .catch(({ response }) => {
                handleAxiosError({response: response})
            })
            .finally(() => setIsLoading(false))
    }

    const getProductionTypes = async (cid) => {
        setIsLoading(true)
        await axios.get(`/api/production-types?company_id=${cid}`, config)
            .then(res => {
                const types = res.data
                setCompanyProductionTypes(types)
            })
            .finally(() => setIsLoading(false))
    }

    const getGradeCodes = async () => {
        await gradeCodesAPI.getGradeCodes(config)
            .then((data) => {
                setGradeCodes(data)
            })
    }

    const updateProductionEmployee = async (e) => {
        e.preventDefault();

        /* if (!no) {
            setErrorNo(true)
        } else */ if (!name) {
            setErrorName(true)
        } else if (!companyId) {
            setErrorCompany(true)
        } else if (!siteId) {
            setErrorSite(true)
        } else if (productionTypes.length < 1) {
            setErrorProductionTypes(true)
        } else {
            setIsLoading(true)

            const formData = {}

            /* formData['production_employee_no'] = no */
            formData['name'] = name
            formData['company_id'] = companyId
            formData['site_id'] = siteId
            formData['production_types'] = productionTypes

            formData['payment_method'] = paymentMethod
            formData['rates'] = rates

            if(paymentMethod === 'hourly_rate') {
                formData['default_hourly_rate'] = defaultHourlyRate
            }

            await axios
                .put(`/api/production-employees/${params.id}`, formData, config)
                .then(({ data }) => {
                    Swal.fire({
                        icon: "success",
                        customClass: 'success',
                        showCloseButton: true,
                        iconColor: '#00B78E',
                        text: data.success.message
                    })
                })
                .catch(({ response }) => {
                    handleAxiosError({ response: response })
                })
                .finally(() => setIsLoading(false))
        }
    }

    const handleChangeCompany = (event) => {
        setCompanyId(event.target.value)
        setErrorCompany(false)
        setProductionTypes([])
        setSiteId('')
        setErrorSite(false)
        getSites(event.target.value)
    }

    const handleChangeTypes = (event, values) => {
        const { target: { value } } = event
        let duplicateRemoved = []
        value.forEach((type) => {
            if (duplicateRemoved.findIndex((o) => o.id === type.id) >= 0) {
                duplicateRemoved = duplicateRemoved.filter((x) => x.id === type.id)
            } else {
                duplicateRemoved.push(type)
            }
        })

        if (errorProductionTypes) setErrorProductionTypes(false)

        if (value[value.length - 1] === 'all') {
            setProductionTypes(productionTypes.length === typeOptions.length ? [] : typeOptions)
            return
        }

        setProductionTypes(duplicateRemoved)
    }

    const handleDeleteSelection = (e, id) => {
        const shouldDelete = productionTypes.find((x) => x.id === id)
        if (shouldDelete) {
            const filtered = productionTypes.filter((x) => x.id !== shouldDelete.id)
            if (filtered.length > 0) {
                setProductionTypes(filtered)
            } else {
                setProductionTypes([])
                setOpenProductionTypes(false)
            }
        }
    }

    const handleClearPaymentMethod = () => {
        setPaymentMethod('')
        setRates([])
    }

    const handleChangeTabValue = (event, newValue) => {
        setTabValue(newValue);
    }

    const handleAddRate = (gradeCode, rate) => {
        setRates((prev) => [
            ...prev,
            {
                id: generateRandomId(rates),
                grade_code_code: gradeCode?.code,
                grade_code_id: gradeCode?.id,
                rate: rate
            }
        ])
    }

    const generateRandomId = (rates = []) => {
        const ids = rates.map((rate) => rate.id);

        let randomId = Math.floor(Math.random() * 9999);
        while(ids.includes(randomId)) {
            randomId = Math.floor(Math.random() * 9999);
        }
        return randomId
    }

    const handleRemoveRate = (gradeCodeId) => {
        setRates((prev) => prev.filter((rate) => rate.grade_code_id !== gradeCodeId))
    }

    const handleChangeRate = (gradeCodeId, newRate) => {
        const ratesCopy = [...rates]
        const rate = ratesCopy.find(item => item.grade_code_id === gradeCodeId)

        rate.rate = toNumber(newRate)

        setRates(ratesCopy)
    }

    const columns = [
        {
            field: 'grade_code_code',
            headerName: t('grade_code'),
            flex: 1,
        },
        {
            field: 'rate',
            headerName: t('rate'),
            flex: 1,
            renderCell: ((params) => <RateCell params={params} handleChange={handleChangeRate} />)
        },
        {
            field: 'actions',
            headerName: t('actions'),
            flex: 0.5,
            minWidth: 50,
            renderCell: (params) => <RatesActions params={params} handleDelete={handleRemoveRate} />
        }
    ];

    return (
        <>
            {isLoading ? <Loading /> : ''}
            <Box>
                <div className='flex border-b justify-between items-center p-5 w-full bg-white' style={{ borderRadius: '5px 5px 0 0' }}>
                    <div className='flex items-center justify-between w-full leading-none'>
                        <p className='text-[18px] font-[600] text-[#333333] flex items-center'><span className='text-[#336195] text-[35px] mr-4'>•</span> {t('update_production_employee')} - {name}</p>
                        <div style={{ transform: "rotate(45deg)" }} className="font-[600] text-[#333333]">
                            <Link to="/production-employees"><i className="fa-solid fa-plus"></i></Link>
                        </div>
                    </div>
                </div>
                <div className='w-full bg-white h-full'>
                    <TabContext value={tabValue}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <TabList onChange={handleChangeTabValue} variant='scrollable'>
                                <Tab className='!normal-case' value="general" label={t('general')} icon={<i className="fa-solid fa-circle-info"></i>} iconPosition='start' />
                                <Tab className='!normal-case' value="payroll" label={t('payroll')} icon={<i className="fa-solid fa-money-bill"></i>} iconPosition='start' />
                            </TabList>
                        </Box>

                        <TabPanel value="general">
                            <form className='flex justify-center flex-col items-start mt-2'>
                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('no')} *</p>
                                    <div className='w-full'>
                                        <TextField
                                            sx={TEXTFIELD_STYLE}
                                            type="text"
                                            name="production_employee_no"
                                            value={no}
                                            onChange={(e) => { setNo(e.target.value); setErrorNo(false) }}
                                            disabled
                                        />
                                        {errorNo ? (
                                            <FormHelperText>{t('please_enter_the_x', { x: t('no') })}</FormHelperText>
                                        ) : (
                                            ''
                                        )}
                                    </div>
                                </div>

                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('name')} *</p>
                                    <div className='w-full'>
                                        <TextField
                                            sx={TEXTFIELD_STYLE}
                                            type="text"
                                            name="production_employee_name"
                                            value={name}
                                            onChange={(e) => { setName(e.target.value); setErrorName(false) }}
                                        />
                                        {errorName ? (
                                            <FormHelperText>{t('please_enter_the_x', { x: t('name') })}</FormHelperText>
                                        ) : (
                                            ''
                                        )}
                                    </div>
                                </div>

                                {(user?.role !== 'production_supervisor' && user?.role !== 'office_manager') ? (
                                    <div className='w-full pb-4'>
                                        <p className="text-[14px] text-[#718096]">{t('company')} *</p>
                                        <div className='w-full'>
                                            <FormControl sx={{ width: '100%' }}>
                                                <Select value={companyId} onChange={handleChangeCompany}
                                                    sx={{
                                                        width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '9px !important' },
                                                        boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                                    }}>
                                                    {companies.map((item, index) => <MenuItem key={index} value={item.id}>{item.name}</MenuItem>)}
                                                </Select>
                                                {errorCompany ? (
                                                    <FormHelperText>{t('please_choose_the_x', { x: t('company') })}</FormHelperText>
                                                ) : (
                                                    ''
                                                )}
                                            </FormControl>

                                        </div>
                                    </div>
                                ) : ''}
                                {user?.role !== 'production_supervisor' ? (
                                    <div className='w-full pb-4'>
                                        <p className="text-[14px] text-[#718096]">{t('site')} *</p>
                                        <div className='w-full'>
                                            <FormControl sx={{ width: '100%' }}>
                                                <Select
                                                    disabled={!(companyId && companyId !== '')}
                                                    value={siteId}
                                                    onChange={e => { setSiteId(e.target.value); setErrorSite(false) }}
                                                    sx={{
                                                        width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '9px !important' },
                                                        boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                                    }}>
                                                    {(companyId !== "" && companyId) ?
                                                        sites.map((item, index) => <MenuItem key={index} value={item.id}>{item.name}</MenuItem>)
                                                        : ''}
                                                </Select>
                                                {errorSite ? (
                                                    <FormHelperText>{t('please_choose_the_x', { x: t('site') })}</FormHelperText>
                                                ) : (
                                                    ''
                                                )}
                                            </FormControl>
                                        </div>
                                    </div>
                                ) : (
                                    ''
                                )}

                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('production_type')} *</p>
                                    <div className='w-full'>
                                        <FormControl sx={{ width: '100%' }}>
                                            <Select
                                                open={openProductionTypes}
                                                onOpen={() => setOpenProductionTypes(true)}
                                                onClose={() => setOpenProductionTypes(false)}
                                                disabled={!((user?.role !== 'production_supervisor' || user?.role !== 'office_manager') && (companyId && companyId !== ''))}
                                                multiple
                                                value={productionTypes}
                                                onChange={handleChangeTypes}
                                                renderValue={(selected) => (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {selected.map((x) => (
                                                            <Chip
                                                                key={`pi_option-${x.id}`}
                                                                label={x.code}
                                                                onMouseDown={(e) => e.stopPropagation()}
                                                                onDelete={(e) => handleDeleteSelection(e, x.id)}
                                                            />
                                                        ))}
                                                    </Box>
                                                )}
                                                sx={SELECT_STYLE}
                                            >
                                                {
                                                    companyProductionTypes?.length > 0 ?
                                                        <MenuItem value='all'>
                                                            <ListItemIcon>
                                                                <Checkbox checked={everyProductionTypeSelected} indeterminate={productionTypes.length > 0 && productionTypes.length < companyProductionTypes.length} />
                                                            </ListItemIcon>
                                                            <ListItemText primary={t('select_all')} />
                                                        </MenuItem>
                                                        :
                                                        <MenuItem value='nooptions' disabled sx={{ textDecoration: 'italic' }}>{t('no_options')}</MenuItem>

                                                }
                                                {
                                                    companyProductionTypes.map((type) =>
                                                        <MenuItem value={type} key={`pt${type.id}`}>
                                                            <Checkbox checked={productionTypes?.findIndex((i) => i.id === type.id) >= 0} />
                                                            <ListItemText primary={type.code} />
                                                        </MenuItem>
                                                    )
                                                }
                                            </Select>
                                            {errorProductionTypes ? (
                                                <FormHelperText>{t('please_choose_at_least_x_y', {
                                                    x: 1,
                                                    y: t('production_type')
                                                })}</FormHelperText>
                                            ) : (
                                                ''
                                            )}
                                        </FormControl>
                                    </div>
                                </div>
                            </form>
                        </TabPanel>

                        <TabPanel value="payroll">
                            <form className='flex justify-center flex-col items-start'>
                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('payment_method')}</p>
                                    <div className='w-full'>
                                        <FormControl sx={{ width: '100%' }}>
                                            <Select
                                                value={paymentMethod}
                                                onChange={e => setPaymentMethod(e.target.value)}
                                                sx={{
                                                    ...SELECT_STYLE,
                                                    ".MuiSelect-iconOutlined": {
                                                        display: isValid(paymentMethod) ? 'none !important' : ''
                                                    },
                                                }}
                                                endAdornment={isValid(paymentMethod) ? (<IconButton sx={{ padding: '0' }} onClick={handleClearPaymentMethod}><ClearIcon /></IconButton>) : undefined}
                                            >
                                                {
                                                    PAYMENT_METHODS.map((pm, index) =>
                                                        <MenuItem key={`pm-${index}`} value={pm.name}>{t(pm.name)}</MenuItem>
                                                    )
                                                }
                                            </Select>
                                        </FormControl>
                                    </div>
                                </div>

                                {['piece_rate'].includes(paymentMethod) && (
                                    <div className='w-full pb-4'>
                                        <div className='flex flex-row justify-between items-center pb-2'>
                                            <p className="text-[14px] text-[#718096]">{t('rates')} ({currencySymbol})</p>
                                            <AddButton
                                                onClick={() => setOpenAddRatesModal(true)}
                                            >
                                                <i className="fa-solid fa-plus" />
                                            </AddButton>
                                        </div>

                                        <TableShared
                                            items={rates}
                                            columns={columns}
                                        />
                                    </div>
                                )}

                                {['hourly_rate'].includes(paymentMethod) && (
                                    <div className='w-full pb-4'>
                                        <p className="text-[14px] text-[#718096]">{t('default_hourly_rate')}</p>
                                        <TextField
                                            type="text"
                                            sx={TEXTFIELD_STYLE}
                                            name="default_hourly_rate"
                                            value={defaultHourlyRate}
                                            onChange={(e) => handleNumber({
                                                event: e,
                                                setter: setDefaultHourlyRate,
                                                precision: 2
                                            })}
                                            onBlur={(e) => handleZero({
                                                event: e,
                                                setter: setDefaultHourlyRate
                                            })}
                                            onFocus={e => deleteZero({
                                                event: e,
                                                setter: setDefaultHourlyRate,
                                                value: defaultHourlyRate
                                            })}
                                        />
                                    </div>
                                )}
                            </form>
                        </TabPanel>
                    </TabContext>
                    <div className='w-full flex justify-end px-6 pb-14'>
                        <button type="button" onClick={updateProductionEmployee} className='text-white font-[700] rounded-md py-2.5 px-5 uppercase text-[14px] bg-[#015D9F] hover:bg-[#003459] disabled:bg-[#B8B7BC]'>
                            <span className='pr-2'><i className="fa-solid fa-clock-rotate-left"></i></span> {t('update')}
                        </button>
                    </div>
                </div>
            </Box>
             <AddRateModal
                open={openAddRatesModal}
                setOpen={setOpenAddRatesModal}
                handleAdd={handleAddRate}
                gradeCodes={gradeCodes}
                rates={rates}
                currencySymbol={currencySymbol}
            />
        </>
    )
}

export default UpdateProductionEmployee

const RateCell = ({params, handleChange}) => {
    const start = 0
    const end = 5
    const precision = 2

    const [rate, setRate] = useState(params.value)

    const handleZeroLocal = (e) => {
        if (e.target.value === "" || e.target.value === '-') {
            setRate(0)

            handleChange(params.row.grade_code_id, 0)
        }
    }

    const handleChangeRate = (e) => {
        const regex = createRegexFromString(`/^(-?\\d{${start},${end}})((\\.(\\d{0,${precision}})?)?)$/i`)
        if (regex.test(e.target.value)) {
            if (e.target.value < 0 || e.target.value === '-') {
                setRate(0)
            } else {
                setRate(e.target.value)
                handleChange(params.row.grade_code_id, e.target.value)
            }
        }
    }

    return (
        <TextField
            type="text"
            variant='standard'
            className='w-2/3 mb-5 px-0 pt-0'
            name="rate"
            value={rate}
            onChange={(e) => handleChangeRate(e) }
            onBlur={handleZeroLocal}
            onFocus={e => deleteZero({
                event: e,
                setter: setRate,
                value: rate
            })}
        />
    )
}

const RatesActions = ({ params, handleDelete }) => {
    return (
        <>
            <div className='flex justify-between'>
                <Tooltip
                    disableInteractive
                    title={t('delete')}
                    placement='bottom'
                >
                    <div
                        style={{
                            color: 'rgba(0,0,0,.54)'
                        }}
                    >
                        <span
                            style={{ cursor: 'pointer' }}
                            className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200"
                            onClick={() => handleDelete(params.row.grade_code_id)}
                        >
                            <i className="fa-solid fa-trash" />
                        </span>
                    </div>
                </Tooltip>
            </div>
        </>
    )
}
